import React, { useState, useEffect } from 'react';
import { settingsApi } from '../lib/api';
import { Save, AlertCircle, Check, RefreshCw } from 'lucide-react';

const currencySymbols = { USD: '$', INR: '₹', EUR: '€', GBP: '£' };

// Input component - defined outside to prevent re-creation on each render
function InputField({ label, value, onChange, type = 'number', suffix, prefix, min = 0, step = 'any', help }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm font-medium text-slate-300 mb-2">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          min={min}
          step={step}
          className={`w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 text-slate-200
            focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all
            ${prefix ? 'pl-10 pr-4' : 'px-4'} ${suffix ? 'pr-16' : ''}`}
        />
        {suffix && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{suffix}</span>}
      </div>
      {help && <p className="mt-1.5 text-xs text-slate-500">{help}</p>}
    </div>
  );
}

function SelectField({ label, value, onChange, options, help }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm font-medium text-slate-300 mb-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 text-slate-200
          focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {help && <p className="mt-1.5 text-xs text-slate-500">{help}</p>}
    </div>
  );
}

function SettingsPage() {
  const [settings, setSettings] = useState({
    usdInrRate: 83.5,
    eurInrRate: 90.5,
    gbpInrRate: 105.5,
    targetCurrency: 'INR',
    profitMarginPercentage: 30,
    bandwidthCostPerTB: 500,
    ipAddressCost: 100,
    backupCostPerGB: 2,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await settingsApi.get();
      if (data.settings) {
        setSettings({
          usdInrRate: data.settings.usd_inr_rate,
          eurInrRate: data.settings.eur_inr_rate,
          gbpInrRate: data.settings.gbp_inr_rate,
          targetCurrency: data.settings.target_currency,
          profitMarginPercentage: data.settings.profit_margin_percentage,
          bandwidthCostPerTB: data.settings.bandwidth_cost_per_tb,
          ipAddressCost: data.settings.ip_address_cost,
          backupCostPerGB: data.settings.backup_cost_per_gb,
        });
      }
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await settingsApi.update(settings);
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Global Settings</h1>
          <p className="text-slate-400 mt-1">Configure default values used across all pricing calculations</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 
            text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 mb-6 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">×</button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 mb-6 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400">
          <Check className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      <div className="space-y-8">
        {/* Exchange Rates */}
        <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-white">Exchange Rates</h2>
              <p className="text-sm text-slate-400 mt-1">Currency conversion rates for cost calculations</p>
            </div>
            <button
              onClick={() => {
                // Could fetch live rates from an API in a real app
                alert('In a production app, this would fetch live exchange rates from an API');
              }}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-400 hover:text-amber-400 
                bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              Update Rates
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField
              label="USD to INR"
              value={settings.usdInrRate}
              onChange={(v) => updateSetting('usdInrRate', v)}
              step="0.01"
              help="1 USD = X INR"
            />
            <InputField
              label="EUR to INR"
              value={settings.eurInrRate}
              onChange={(v) => updateSetting('eurInrRate', v)}
              step="0.01"
              help="1 EUR = X INR"
            />
            <InputField
              label="GBP to INR"
              value={settings.gbpInrRate}
              onChange={(v) => updateSetting('gbpInrRate', v)}
              step="0.01"
              help="1 GBP = X INR"
            />
          </div>
        </div>

        {/* Display Settings */}
        <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-white">Display Settings</h2>
            <p className="text-sm text-slate-400 mt-1">Default display preferences</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SelectField
              label="Default Display Currency"
              value={settings.targetCurrency}
              onChange={(v) => updateSetting('targetCurrency', v)}
              options={[
                { value: 'INR', label: 'INR - Indian Rupee (₹)' },
                { value: 'USD', label: 'USD - US Dollar ($)' },
                { value: 'EUR', label: 'EUR - Euro (€)' },
                { value: 'GBP', label: 'GBP - British Pound (£)' },
              ]}
              help="All prices will be displayed in this currency"
            />
            <InputField
              label="Default Profit Margin"
              value={settings.profitMarginPercentage}
              onChange={(v) => updateSetting('profitMarginPercentage', v)}
              suffix="%"
              help="Added to cost price to calculate selling price"
            />
          </div>
        </div>

        {/* Additional Costs */}
        <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800/50 rounded-2xl p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-white">Additional Service Costs</h2>
            <p className="text-sm text-slate-400 mt-1">Default costs for additional services (per month)</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField
              label="Bandwidth Cost"
              value={settings.bandwidthCostPerTB}
              onChange={(v) => updateSetting('bandwidthCostPerTB', v)}
              prefix={currencySymbols[settings.targetCurrency]}
              suffix="per TB"
              help="Monthly cost per TB of bandwidth"
            />
            <InputField
              label="IP Address Cost"
              value={settings.ipAddressCost}
              onChange={(v) => updateSetting('ipAddressCost', v)}
              prefix={currencySymbols[settings.targetCurrency]}
              suffix="per IP"
              help="Monthly cost per public IP address"
            />
            <InputField
              label="Backup Cost"
              value={settings.backupCostPerGB}
              onChange={(v) => updateSetting('backupCostPerGB', v)}
              prefix={currencySymbols[settings.targetCurrency]}
              suffix="per GB"
              help="Monthly backup storage cost per GB"
            />
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6">
          <h3 className="text-amber-400 font-semibold mb-2">About Global Settings</h3>
          <p className="text-sm text-slate-300 leading-relaxed">
            These settings apply as defaults across all your server configurations. Individual configurations 
            can override these values when needed. Exchange rates are used to convert costs entered in different 
            currencies to your preferred display currency for consistent pricing calculations.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
